import { useState, useEffect } from 'react'
import { useSaveStore } from '../stores/saveStore'
import { useGameStore } from '../stores/gameStore'
import { useCloudSaveStore } from '../stores/cloudSaveStore'
import { useAuthStore } from '../stores/authStore'
import type { GameSave } from '../lib/supabase'
import './SaveManager.css'

interface SaveManagerProps {
  isOpen: boolean
  onClose: () => void
}

export function SaveManager({ isOpen, onClose }: SaveManagerProps) {
  const {
    autoSave,
    slots,
    saveToSlot,
    loadFromSlot,
    deleteSlot,
    loadAutoSave,
    initSession,
  } = useSaveStore()

  const gameState = useGameStore()
  const user = useAuthStore((state) => state.user)

  const {
    cloudSaves,
    isLoading: isCloudLoading,
    error: cloudError,
    fetchCloudSaves,
    uploadToCloud,
    downloadFromCloud,
    deleteCloudSave,
    clearError,
  } = useCloudSaveStore()

  const [selectedSlot, setSelectedSlot] = useState<number | null>(null)
  const [showNameDialog, setShowNameDialog] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const [slotName, setSlotName] = useState('')
  const [dialogMode, setDialogMode] = useState<'save' | 'load'>('save')

  // 初始化游戏会话
  useEffect(() => {
    if (isOpen) {
      initSession()
      // 如果已登录，获取云端存档
      if (user) {
        fetchCloudSaves()
      }
    }
  }, [isOpen, initSession, user, fetchCloudSaves])

  if (!isOpen) return null

  // 格式化时间
  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp)
    return date.toLocaleString('zh-CN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }



  // 处理点击自动存档
  const handleAutoSaveClick = () => {
    if (!autoSave) return
    setDialogMode('load')
    setShowConfirmDialog(true)
  }

  // 处理点击槽位
  const handleSlotClick = (index: number) => {
    setSelectedSlot(index)
    const slot = slots[index]

    if (slot) {
      // 有存档，询问是否读取
      setDialogMode('load')
      setShowConfirmDialog(true)
    } else {
      // 空槽位，直接保存
      setDialogMode('save')
      setSlotName(`存档 ${index + 1}`)
      setShowNameDialog(true)
    }
  }

  // 处理保存按钮
  const handleSaveClick = () => {
    if (selectedSlot === null) return
    const slot = slots[selectedSlot]
    setDialogMode('save')
    setSlotName(slot?.name || `存档 ${selectedSlot + 1}`)
    setShowNameDialog(true)
  }

  // 处理确认保存
  const handleConfirmSave = () => {
    if (selectedSlot === null || !slotName.trim()) return

    const gameData = {
      currentView: gameState.currentView,
      isPaused: gameState.isPaused,
      gameSpeed: gameState.gameSpeed,
      gameDate: gameState.gameDate,
      resources: gameState.resources,
      resourceIncome: gameState.resourceIncome,
      buildings: gameState.buildings,
      populationAllocation: gameState.populationAllocation,
      talents: gameState.talents,
      availableTalents: gameState.availableTalents,
      recruitCooldown: gameState.recruitCooldown,
    }

    saveToSlot(selectedSlot, slotName.trim(), gameData)
    setShowNameDialog(false)
    setSlotName('')
  }

  // 处理确认读取
  const handleConfirmLoad = () => {
    let gameData: Partial<typeof gameState> | null = null

    if (dialogMode === 'load' && selectedSlot !== null) {
      const slotData = loadFromSlot(selectedSlot)
      gameData = slotData?.gameData || null
    } else if (dialogMode === 'load' && autoSave) {
      // 从自动存档加载
      const autoSaveData = loadAutoSave()
      gameData = autoSaveData?.gameData || null
    }

    if (gameData) {
      // 使用 restoreGameState 恢复完整游戏状态
      gameState.restoreGameState(gameData)
    }

    setShowConfirmDialog(false)
    onClose()
  }

  // 处理删除存档
  const handleDeleteClick = () => {
    if (selectedSlot === null) return
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = () => {
    if (selectedSlot === null) return
    deleteSlot(selectedSlot)
    setShowDeleteConfirm(false)
    setSelectedSlot(null)
  }

  // 处理上传到云端
  const handleUploadToCloud = async () => {
    if (selectedSlot === null || !slots[selectedSlot]) return
    const success = await uploadToCloud(selectedSlot, slots[selectedSlot]!)
    if (success) {
      alert('上传成功！')
    }
  }

  // 处理从云端下载
  const handleDownloadFromCloud = async (cloudSave: GameSave) => {
    const localSave = await downloadFromCloud(cloudSave)
    if (localSave) {
      // 保存到对应槽位
      const slotIndex = cloudSave.slot_number - 1
      saveToSlot(slotIndex, localSave.name, localSave.gameData)
      alert('下载成功！')
    }
  }

  // 处理删除云端存档
  const handleDeleteCloudSave = async (saveId: string) => {
    if (confirm('确定要删除云端存档吗？')) {
      await deleteCloudSave(saveId)
    }
  }

  return (
    <div className="save-overlay">
      <div className="save-panel">
        <button className="save-close-btn" onClick={onClose}>
          ×
        </button>

        <h2 className="save-title">存档管理</h2>

        {/* 自动存档 */}
        <div className="save-section">
          <h3 className="save-section-title">进行中（自动存档）</h3>
          <div
            className={`autosave-card ${autoSave ? '' : 'empty'}`}
            onClick={handleAutoSaveClick}
          >
            {autoSave ? (
              <div className="autosave-info">
                <div className="autosave-label">自动存档</div>
                <div className="autosave-time">
                  上次保存：{formatTime(autoSave.lastSaveAt)}
                </div>
                <div className="autosave-preview">
                  <span className="autosave-preview-item">
                    人口：{autoSave.gameData.resources?.population || 0}
                  </span>
                  <span className="autosave-preview-item">
                    金钱：{autoSave.gameData.resources?.money || 0}
                  </span>
                  <span className="autosave-preview-item">
                    粮食：{autoSave.gameData.resources?.food || 0}
                  </span>
                </div>
              </div>
            ) : (
              <div className="autosave-empty">暂无自动存档</div>
            )}
          </div>
        </div>

        {/* 存档槽位 */}
        <div className="save-section">
          <h3 className="save-section-title">存档槽位</h3>
          <div className="save-slots-grid">
            {slots.map((slot, index) => (
              <div
                key={index}
                className={`save-slot ${slot ? '' : 'empty'} ${
                  selectedSlot === index ? 'active' : ''
                }`}
                onClick={() => handleSlotClick(index)}
              >
                <div className="save-slot-number">{index + 1}</div>
                {slot ? (
                  <>
                    <div className="save-slot-name">{slot.name}</div>
                    <div className="save-slot-time">
                      {formatTime(slot.updatedAt)}
                    </div>
                    <div className="save-slot-preview">
                      <div className="save-slot-preview-item">
                        <span>年份</span>
                        <span>
                          {slot.gameYear > 0
                            ? `${slot.gameYear}年`
                            : `公元前${Math.abs(slot.gameYear)}年`}
                        </span>
                      </div>
                      <div className="save-slot-preview-item">
                        <span>人口</span>
                        <span>{slot.preview.totalPopulation}</span>
                      </div>
                      <div className="save-slot-preview-item">
                        <span>金钱</span>
                        <span>{slot.preview.totalMoney}</span>
                      </div>
                      <div className="save-slot-preview-item">
                        <span>人才</span>
                        <span>{slot.preview.talentCount}人</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="save-slot-empty-text">空槽位</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 云端存档区域 */}
        {user && (
          <div className="save-section">
            <h3 className="save-section-title">
              云端存档
              {isCloudLoading && <span className="cloud-loading">同步中...</span>}
            </h3>
            {cloudError && (
              <div className="cloud-error" onClick={clearError}>
                {cloudError}
              </div>
            )}
            <div className="cloud-saves-grid">
              {cloudSaves.length === 0 ? (
                <div className="cloud-empty">暂无云端存档</div>
              ) : (
                cloudSaves.map((cloudSave) => (
                  <div key={cloudSave.id} className="cloud-save-card">
                    <div className="cloud-save-header">
                      <span className="cloud-save-name">{cloudSave.save_name}</span>
                      <span className="cloud-save-slot">槽位 {cloudSave.slot_number}</span>
                    </div>
                    <div className="cloud-save-time">
                      {formatTime(new Date(cloudSave.updated_at).getTime())}
                    </div>
                    <div className="cloud-save-preview">
                      <span>人口: {cloudSave.preview.totalPopulation || 0}</span>
                      <span>金钱: {cloudSave.preview.totalMoney || 0}</span>
                      <span>人才: {cloudSave.preview.talentCount || 0}人</span>
                    </div>
                    <div className="cloud-save-actions">
                      <button
                        className="cloud-btn cloud-btn-download"
                        onClick={() => handleDownloadFromCloud(cloudSave)}
                        disabled={isCloudLoading}
                      >
                        下载
                      </button>
                      <button
                        className="cloud-btn cloud-btn-delete"
                        onClick={() => handleDeleteCloudSave(cloudSave.id)}
                        disabled={isCloudLoading}
                      >
                        删除
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* 操作按钮 */}
        <div className="save-actions">
          {selectedSlot !== null && slots[selectedSlot] && (
            <>
              <button
                className="save-btn save-btn-primary"
                onClick={handleSaveClick}
              >
                覆盖存档
              </button>
              <button
                className="save-btn save-btn-secondary"
                onClick={() => setShowConfirmDialog(true)}
              >
                读取存档
              </button>
              {user && (
                <button
                  className="save-btn save-btn-cloud"
                  onClick={handleUploadToCloud}
                  disabled={isCloudLoading}
                >
                  {isCloudLoading ? '上传中...' : '上传到云端'}
                </button>
              )}
              <button
                className="save-btn save-btn-danger"
                onClick={handleDeleteClick}
              >
                删除
              </button>
            </>
          )}
          {selectedSlot !== null && !slots[selectedSlot] && (
            <>
              <button
                className="save-btn save-btn-primary"
                onClick={handleSaveClick}
              >
                保存到新槽位
              </button>
              {user && (
                <button
                  className="save-btn save-btn-cloud"
                  onClick={() => alert('请先保存到本地槽位，再上传到云端')}
                >
                  上传到云端
                </button>
              )}
            </>
          )}
          <button className="save-btn save-btn-secondary" onClick={onClose}>
            关闭
          </button>
        </div>
      </div>

      {/* 命名对话框 */}
      {showNameDialog && (
        <div className="save-dialog-overlay">
          <div className="save-dialog">
            <h3 className="save-dialog-title">命名存档</h3>
            <input
              type="text"
              className="save-dialog-input"
              value={slotName}
              onChange={(e) => setSlotName(e.target.value)}
              placeholder="输入存档名称"
              maxLength={20}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleConfirmSave()
                if (e.key === 'Escape') setShowNameDialog(false)
              }}
            />
            <div className="save-dialog-actions">
              <button
                className="save-btn save-btn-primary"
                onClick={handleConfirmSave}
                disabled={!slotName.trim()}
              >
                确认
              </button>
              <button
                className="save-btn save-btn-secondary"
                onClick={() => setShowNameDialog(false)}
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 确认读取对话框 */}
      {showConfirmDialog && (
        <div className="save-dialog-overlay">
          <div className="save-dialog">
            <h3 className="save-dialog-title">确认读取</h3>
            <p className="save-confirm-text">
              读取存档将覆盖当前游戏进度。
              <br />
              当前进度将自动保存到「进行中」。
              <br />
              是否继续？
            </p>
            <div className="save-dialog-actions">
              <button
                className="save-btn save-btn-primary"
                onClick={handleConfirmLoad}
              >
                确认读取
              </button>
              <button
                className="save-btn save-btn-secondary"
                onClick={() => setShowConfirmDialog(false)}
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 删除确认对话框 */}
      {showDeleteConfirm && (
        <div className="save-dialog-overlay">
          <div className="save-dialog">
            <h3 className="save-dialog-title">确认删除</h3>
            <p className="save-confirm-text">
              确定要删除存档「{slots[selectedSlot!]?.name}」吗？
              <br />
              此操作不可恢复。
            </p>
            <div className="save-dialog-actions">
              <button
                className="save-btn save-btn-danger"
                onClick={handleConfirmDelete}
              >
                确认删除
              </button>
              <button
                className="save-btn save-btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
